import { AddRegular, DeleteRegular, EditRegular } from "@fluentui/react-icons";
import {
  adminDeleteContextFileTag,
  getProjectIqContextFileTags,
} from "apps/project-iq/api";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import {
  IAdminListAction,
  IAdminRowAction,
} from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { IContextFileTag } from "models/IContextFileTag";
import { ITableColumn } from "models/ITableColumn";
import { useEffect, useMemo, useState } from "react";
import { EditContextFileTagDialog } from "./EditContextFileTagDialog";
import { useManageContextFileTagsStyles } from "./ManageContextFileTags.styles";
import { IDialogState } from "./ManageContextFileTags.types";

const TAGS_PER_PAGE = 15;
const TAGS_ACTIONS_COLUMN_WIDTH = "115px";

export const ManageContextFileTags = (): JSX.Element => {
  const colStyles = useAdminColumnStyles();
  const tagStyles = useManageContextFileTagsStyles();
  const [tags, setTags] = useState<IContextFileTag[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<IDialogState>({
    open: false,
    tag: null,
  });
  const [deleteTarget, setDeleteTarget] = useState<IContextFileTag | null>(
    null,
  );

  const loadTags = async (silent = false): Promise<void> => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await getProjectIqContextFileTags();
      setTags(data as IContextFileTag[]);
    } catch {
      setError("Failed to load tags. Please try again.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    void loadTags();
  }, []);

  const handleSave = (): void => {
    setDialogState({ open: false, tag: null });
    void loadTags();
  };

  const handleDismiss = (): void => {
    setDialogState({ open: false, tag: null });
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    try {
      await adminDeleteContextFileTag(deleteTarget.id);
      setDeleteTarget(null);
      void loadTags();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to delete tag. Please try again.",
      );
      setDeleteTarget(null);
    }
  };

  const columns: ITableColumn<IContextFileTag>[] = useMemo(
    () => [
      {
        columnId: "name",
        displayValue: "Tag name",
        className: colStyles.normal,
        renderHeaderCell: () => "Tag name",
        compare: (a: IContextFileTag, b: IContextFileTag) =>
          a.name.localeCompare(b.name),
        renderCell: (item: IContextFileTag) => (
          <span className={tagStyles.nameCell}>{item.name}</span>
        ),
      },
      {
        columnId: "description",
        displayValue: "Description",
        className: colStyles.wide,
        renderHeaderCell: () => "Description",
        compare: (a: IContextFileTag, b: IContextFileTag) =>
          a.description.localeCompare(b.description),
        renderCell: (item: IContextFileTag) => (
          <span className={tagStyles.descriptionCell}>{item.description}</span>
        ),
      },
    ],
    [colStyles, tagStyles],
  );

  const rowActions: IAdminRowAction<IContextFileTag>[] = useMemo(
    () => [
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: (item: IContextFileTag) =>
          setDialogState({ open: true, tag: item }),
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: (item: IContextFileTag) => setDeleteTarget(item),
      },
    ],
    [],
  );

  const headerActions: IAdminListAction[] = useMemo(
    () => [
      {
        label: "Add Tag",
        icon: <AddRegular />,
        appearance: "primary",
        disabled: loading,
        onClick: () => setDialogState({ open: true, tag: null }),
      },
    ],
    [loading],
  );

  return (
    <>
      {error && (
        <div role="alert" className={tagStyles.errorAlert}>
          {error}
        </div>
      )}
      <AdminListPage<IContextFileTag>
        title="Context Files Tags"
        items={tags}
        columns={columns}
        isLoading={loading}
        headerActions={headerActions}
        searchPlaceholder="Search tags..."
        searchQuery=""
        onSearchChange={() => {}}
        rowActions={rowActions}
        getRowId={(item) => item.id}
        paginatedItems={tags}
        selectedPageIndex={0}
        pageCount={1}
        totalItemsCount={tags.length}
        itemsPerPage={TAGS_PER_PAGE}
        onPageChange={() => {}}
        emptyStateMessage="No tags found."
        actionsWidth={TAGS_ACTIONS_COLUMN_WIDTH}
      />
      <EditContextFileTagDialog
        open={dialogState.open}
        tag={dialogState.tag}
        onSave={handleSave}
        onDismiss={handleDismiss}
      />
      <ActionDialog
        isVisible={deleteTarget !== null}
        title="Delete Tag"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => setDeleteTarget(null)}
      />
    </>
  );
};
